import React, { useCallback } from 'react';
import { FunctionComponent } from 'react';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { useTranslation } from 'react-i18next';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { CreateEntityButton } from 'record-it-components/misc/CreateEntityButton/CreateEntityButton';
import { useStopwatchInfos } from '../../../hooks/stopwatch/useStopwatchInfos';
import { StopwatchList } from '../../timeTracking/StopwatchList/StopwatchList';
import { StartNewStopwatchFixedButton } from '../../timeTracking/StartNewStopwatchFixedButton/StartNewStopwatchFixedButton';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { EntityName } from 'record-it-time-common/EntityName';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import {
  MessageBox,
  MessageBoxType
} from 'record-it-components/misc/MessageBox/MessageBox';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { UserGroupUtils } from '../../../classes/entities/UserGroup/UserGroupUtils';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { TodaysWorkTimeInfo } from '../../workTime/TodaysWorkTimeInfo/TodaysWorkTimeInfo';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useStartCreatingWorkTimeEntryWithoutStopwatch } from '../../../hooks/createWorkTimeEntry/useStartCreatingWorkTimeEntryWithoutStopwatch/useStartCreatingWorkTimeEntryWithoutStopwatch';
import styles from './HomePage.module.css';

export type HomePageProps = Record<string, never>;

export const HomePage: FunctionComponent<HomePageProps> = (props) => {
  prepareProps({ props, ignoreChildren: true });

  useConfigureBreadcrumbsWithBuilder(() => {});

  const { t } = useTranslation();

  const infos = useStopwatchInfos();
  const userGroups = useEntities(EntityName.UserGroup, 'getAll');
  const userGroupsAvailable = userGroups.length > 0;

  const { startCreatingWorkTimeEntryWithoutStopwatch } =
    useStartCreatingWorkTimeEntryWithoutStopwatch();

  const handleCreateEntityClicked = useEventCallback(
    (userGroup: UserGroup | null) => {
      assertNotNullOrUndefined(
        userGroup,
        'no userGroup got selected, did you forget to hide the button?'
      );
      startCreatingWorkTimeEntryWithoutStopwatch({ userGroupId: userGroup.id });
    }
  );

  const renderRightContentElements = useCallback(() => {
    const elements: Array<JSX.Element> = [];

    if (userGroupsAvailable) {
      elements.push(
        <CreateEntityButton<UserGroup>
          key="HomePage.CreateWorkTimeEntryButton"
          text={t('components.page.HomePage.createWorkTimeEntry')}
          dropdownItems={userGroups}
          getDropdownItemDisplayText={UserGroupUtils.displayTextGetter}
          getDropdownItemKey={UserGroupUtils.keyGetter}
          onCreateEntity={handleCreateEntityClicked}
          data-testid="HomePage.CreateWorkTimeEntryButton"
        ></CreateEntityButton>
      );
    }

    return elements;
  }, [userGroupsAvailable, userGroups, t, handleCreateEntityClicked]);

  return (
    <PageContentContainer>
      <PageContentHeader
        title={t('components.page.HomePage.title')}
        iconConfig={iconConfigurations.prefixed.page.HomePage.titleIcon}
        renderRightContentElements={renderRightContentElements}
      ></PageContentHeader>
      {!userGroupsAvailable ? (
        <MessageBox
          type={MessageBoxType.Warning}
          data-testid="HomePage.WarningMessageBox"
        >
          {t('components.page.HomePage.noUserGroupsAvailable')}
        </MessageBox>
      ) : null}

      <TodaysWorkTimeInfo
        className={styles.todaysWorkTimeInfo}
      ></TodaysWorkTimeInfo>

      <StopwatchList stopwatchInfos={infos}></StopwatchList>

      {userGroupsAvailable ? (
        <StartNewStopwatchFixedButton data-testid="HomePage.StartNewStopwatchFixedButton"></StartNewStopwatchFixedButton>
      ) : null}
    </PageContentContainer>
  );
};
